import React, { useState, useEffect } from 'react';
import SupplierManagementModal from '../SupplierManagementModal';
import AddMaterialOrderModal from '../AddMaterialOrderModal';
import MaterialOrdersListModal from '../MaterialOrdersListModal';
import './RawMaterialsPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const RawMaterialsPage = () => {
  const [rawMaterials, setRawMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [showOffCutModal, setShowOffCutModal] = useState(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showOrdersListModal, setShowOrdersListModal] = useState(false);

  // Fetch raw materials and suppliers
  useEffect(() => {
    fetchRawMaterials();
    fetchSuppliers();
  }, []);

  const fetchRawMaterials = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching raw materials from:', `${API_URL}/raw-materials`);
      const response = await fetch(`${API_URL}/raw-materials`);
      console.log('Response status:', response.status, response.statusText);
      if (!response.ok) {
        throw new Error(`Failed to fetch raw materials: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('Raw materials data:', data);
      setRawMaterials(data);
    } catch (err) {
      console.error('Error fetching raw materials:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/suppliers`);
      if (!response.ok) {
        throw new Error(`Failed to fetch suppliers: ${response.statusText}`);
      }
      const data = await response.json();
      setSuppliers(data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      // Don't set error state for suppliers, just log it
    }
  };

  // Delete raw material
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this raw material?')) return;
    
    try {
      const response = await fetch(`${API_URL}/raw-materials/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete raw material');
      }
      
      fetchRawMaterials(); // Refresh list
    } catch (err) {
      setError(err.message);
      console.error('Error deleting raw material:', err);
    }
  };

  // Create off-cut
  const handleCreateOffCut = async (parentId, offCutData) => {
    try {
      const response = await fetch(`${API_URL}/raw-materials/${parentId}/off-cut`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(offCutData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create off-cut');
      }
      
      setShowOffCutModal(null);
      fetchRawMaterials(); // Refresh list
    } catch (err) {
      setError(err.message);
      console.error('Error creating off-cut:', err);
    }
  };

  // Handle suppliers changed (from SupplierManagementModal)
  const handleSuppliersChanged = () => {
    fetchSuppliers(); // Refresh suppliers list
  };

  return (
    <div className="raw-materials-page" style={{
      padding: '20px 40px',
      maxWidth: '100%',
      margin: '0 auto'
    }}>
      <div className="page-header" style={{
        marginBottom: '32px',
        textAlign: 'center'
      }}>
        <h1 style={{
          color: '#1976d2',
          fontWeight: 800,
          fontSize: '32px',
          marginBottom: '8px',
          margin: 0
        }}>🗞️ Raw Materials Management</h1>
        <p style={{
          color: '#666',
          fontSize: '16px',
          margin: '8px 0 0 0'
        }}>Manage paper rolls, track usage, and handle off-cuts with full traceability</p>
      </div>

      {error && (
        <div className="error-message" style={{
          background: '#ffebee', 
          color: '#c62828', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #ffcdd2'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="content-header" style={{
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px'
      }}>
        <h2 style={{color: '#1976d2', margin: 0}}>Raw Materials</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            className="btn-secondary"
            onClick={() => setShowSupplierModal(true)}
            style={{
              background: '#1976d2',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            🏢 Manage Suppliers
          </button>
          <button 
            onClick={() => setShowOrderModal(true)}
            style={{
              background: '#ff9800',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            📦 Order Materials
          </button>
          <button 
            onClick={() => setShowOrdersListModal(true)}
            style={{
              background: '#9c27b0',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            📋 View Orders
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            style={{
              background: '#43a047',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            + Add New Material
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading" style={{
          textAlign: 'center', 
          padding: '40px', 
          color: '#666'
        }}>
          Loading raw materials...
        </div>
      ) : (
        <div className="materials-table" style={{
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <table style={{width: '100%', borderCollapse: 'collapse'}}>
            <thead>
              <tr style={{background: '#f5f5f5'}}>
                <th style={{padding: '16px', textAlign: 'left', fontWeight: 'bold'}}>Internal ID</th>
                <th style={{padding: '16px', textAlign: 'left', fontWeight: 'bold'}}>Barcode</th>
                <th style={{padding: '16px', textAlign: 'left', fontWeight: 'bold'}}>Material Type</th>
                <th style={{padding: '16px', textAlign: 'left', fontWeight: 'bold'}}>Dimensions</th>
                <th style={{padding: '16px', textAlign: 'left', fontWeight: 'bold'}}>Supplier</th>
                <th style={{padding: '16px', textAlign: 'left', fontWeight: 'bold'}}>Cost</th>
                <th style={{padding: '16px', textAlign: 'left', fontWeight: 'bold'}}>Work Ticket</th>
                <th style={{padding: '16px', textAlign: 'left', fontWeight: 'bold'}}>Status</th>
                <th style={{padding: '16px', textAlign: 'left', fontWeight: 'bold'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rawMaterials.map(material => (
                <tr key={material.id} style={{borderBottom: '1px solid #eee'}}>
                  <td style={{padding: '16px'}}>
                    <strong style={{color: '#1976d2'}}>{material.internalId}</strong>
                    {material.isOffCut && (
                      <div style={{fontSize: '12px', color: '#ff9800', fontWeight: 'bold'}}>
                        📏 OFF-CUT from {material.parentRoll?.internalId}
                      </div>
                    )}
                  </td>
                  <td style={{padding: '16px', fontFamily: 'monospace', fontSize: '14px'}}>
                    {material.barcode}
                  </td>
                  <td style={{padding: '16px'}}>{material.materialType}</td>
                  <td style={{padding: '16px'}}>
                    <div>{material.width}mm × {material.remainingMeters}m</div>
                    <div style={{fontSize: '12px', color: '#666'}}>
                      (Total: {material.totalMeters}m)
                    </div>
                  </td>
                  <td style={{padding: '16px'}}>{material.supplier?.name || 'Unknown Supplier'}</td>
                  <td style={{padding: '16px'}}>R{parseFloat(material.cost).toFixed(2)}</td>
                  <td style={{padding: '16px'}}>{material.workTicketNr || '-'}</td>
                  <td style={{padding: '16px'}}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      background: material.remainingMeters > 0 ? '#e8f5e8' : '#ffebee',
                      color: material.remainingMeters > 0 ? '#2e7d32' : '#c62828'
                    }}>
                      {material.remainingMeters > 0 ? 'Available' : 'Used Up'}
                    </span>
                  </td>
                  <td style={{padding: '16px'}}>
                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                      <button 
                        onClick={() => setEditingMaterial(material)}
                        style={{
                          padding: '6px 12px',
                          background: '#2196f3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Edit
                      </button>
                      {material.remainingMeters > material.minUsableSize && !material.isOffCut && (
                        <button 
                          onClick={() => setShowOffCutModal(material)}
                          style={{
                            padding: '6px 12px',
                            background: '#ff9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Create Off-Cut
                        </button>
                      )}
                      <button 
                        onClick={() => handleDelete(material.id)}
                        style={{
                          padding: '6px 12px',
                          background: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {rawMaterials.length === 0 && (
            <div style={{
              textAlign: 'center', 
              padding: '40px', 
              color: '#666'
            }}>
              <p>No raw materials found. Add your first roll to get started.</p>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal - Simple inline modal for now */}
      {(showAddModal || editingMaterial) && (
        <RawMaterialModal
          material={editingMaterial}
          suppliers={suppliers}
          onClose={() => {
            setShowAddModal(false);
            setEditingMaterial(null);
          }}
          onSave={() => {
            fetchRawMaterials();
            setShowAddModal(false);
            setEditingMaterial(null);
          }}
        />
      )}

      {/* Off-Cut Modal */}
      {showOffCutModal && (
        <OffCutModal
          parentRoll={showOffCutModal}
          onClose={() => setShowOffCutModal(null)}
          onSave={handleCreateOffCut}
        />
      )}

      {/* Supplier Management Modal */}
      <SupplierManagementModal
        open={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        onSuppliersChange={handleSuppliersChanged}
      />

      {/* Material Order Modal */}
      <AddMaterialOrderModal
        open={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        onOrderAdded={() => {
          setShowOrderModal(false);
          // Optionally refresh materials list if needed
          fetchRawMaterials();
        }}
      />

      {/* Material Orders List Modal */}
      <MaterialOrdersListModal
        open={showOrdersListModal}
        onClose={() => setShowOrdersListModal(false)}
        onOrdersChange={() => {
          // Refresh materials list when orders are updated
          fetchRawMaterials();
        }}
      />
    </div>
  );
};

// Simple Modal Components (inline for now)
const RawMaterialModal = ({ material, suppliers, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    materialType: material?.materialType || '',
    width: material?.width || '',
    totalMeters: material?.totalMeters || '',
    remainingMeters: material?.remainingMeters || '',
    supplierId: material?.supplier?.id || '',
    cost: material?.cost || '',
    workTicketNr: material?.workTicketNr || '',
    notes: material?.notes || '',
    orderDate: material?.orderDate ? material.orderDate.split('T')[0] : '',
    receivedDate: material?.receivedDate ? material.receivedDate.split('T')[0] : new Date().toISOString().split('T')[0]
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = material 
        ? `${API_URL}/raw-materials/${material.id}`
        : `${API_URL}/raw-materials`;
      
      const method = material ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save raw material');
      }
      
      onSave();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white', padding: '24px', borderRadius: '12px',
        width: '90%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto'
      }}>
        <h2>{material ? 'Edit Raw Material' : 'Add New Raw Material'}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px'}}>
            <div>
              <label>Material Type *</label>
              <input
                type="text"
                value={formData.materialType}
                onChange={(e) => setFormData({...formData, materialType: e.target.value})}
                required
                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                placeholder="e.g., Paper, Vinyl, etc."
              />
            </div>
            <div>
              <label>Width (mm) *</label>
              <input
                type="number"
                value={formData.width}
                onChange={(e) => setFormData({...formData, width: e.target.value})}
                required
                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
              />
            </div>
            <div>
              <label>Total Meters *</label>
              <input
                type="number"
                step="0.1"
                value={formData.totalMeters}
                onChange={(e) => setFormData({...formData, totalMeters: e.target.value})}
                required
                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
              />
            </div>
            <div>
              <label>Remaining Meters *</label>
              <input
                type="number"
                step="0.1"
                value={formData.remainingMeters}
                onChange={(e) => setFormData({...formData, remainingMeters: e.target.value})}
                required
                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
              />
            </div>
            <div>
              <label>Supplier *</label>
              <select
                value={formData.supplierId}
                onChange={(e) => setFormData({...formData, supplierId: e.target.value})}
                required
                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
              >
                <option value="">Select a supplier...</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Cost (R) *</label>
              <input
                type="number"
                step="0.01"
                value={formData.cost}
                onChange={(e) => setFormData({...formData, cost: e.target.value})}
                required
                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
              />
            </div>
            <div>
              <label>Work Ticket Nr</label>
              <input
                type="text"
                value={formData.workTicketNr}
                onChange={(e) => setFormData({...formData, workTicketNr: e.target.value})}
                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
              />
            </div>
            <div>
              <label>Received Date</label>
              <input
                type="date"
                value={formData.receivedDate}
                onChange={(e) => setFormData({...formData, receivedDate: e.target.value})}
                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
              />
            </div>
          </div>
          <div style={{marginBottom: '16px'}}>
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows="3"
              style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
            />
          </div>
          <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
            <button type="button" onClick={onClose} style={{
              padding: '10px 20px', background: '#f5f5f5', border: 'none', borderRadius: '6px', cursor: 'pointer'
            }}>
              Cancel
            </button>
            <button type="submit" style={{
              padding: '10px 20px', background: '#43a047', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'
            }}>
              {material ? 'Update' : 'Create'} Material
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const OffCutModal = ({ parentRoll, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    usedMeters: '',
    remainingMeters: '',
    workTicketNr: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(formData.remainingMeters) < parentRoll.minUsableSize) {
      alert(`Off-cut too small. Minimum usable size is ${parentRoll.minUsableSize}mm`);
      return;
    }
    onSave(parentRoll.id, formData);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white', padding: '24px', borderRadius: '12px',
        width: '90%', maxWidth: '500px'
      }}>
        <h2>Create Off-Cut from {parentRoll.internalId}</h2>
        <p style={{color: '#666', marginBottom: '20px'}}>
          Current roll: {parentRoll.width}mm × {parentRoll.remainingMeters}m
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '16px'}}>
            <label>Meters Used *</label>
            <input
              type="number"
              step="0.1"
              value={formData.usedMeters}
              onChange={(e) => setFormData({...formData, usedMeters: e.target.value})}
              required
              style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
            />
          </div>
          <div style={{marginBottom: '16px'}}>
            <label>Remaining Meters for Off-Cut *</label>
            <input
              type="number"
              step="0.1"
              value={formData.remainingMeters}
              onChange={(e) => setFormData({...formData, remainingMeters: e.target.value})}
              required
              style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
            />
            <small style={{color: '#666'}}>Minimum: {parentRoll.minUsableSize}mm</small>
          </div>
          <div style={{marginBottom: '16px'}}>
            <label>Work Ticket Nr</label>
            <input
              type="text"
              value={formData.workTicketNr}
              onChange={(e) => setFormData({...formData, workTicketNr: e.target.value})}
              style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
            />
          </div>
          <div style={{marginBottom: '16px'}}>
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows="3"
              style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
            />
          </div>
          <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
            <button type="button" onClick={onClose} style={{
              padding: '10px 20px', background: '#f5f5f5', border: 'none', borderRadius: '6px', cursor: 'pointer'
            }}>
              Cancel
            </button>
            <button type="submit" style={{
              padding: '10px 20px', background: '#ff9800', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'
            }}>
              Create Off-Cut
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RawMaterialsPage;
